import { FormEvent, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, SubscriptionPackage, TenantWorkspace, WorkspaceMember } from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type PlanDetail = Awaited<ReturnType<typeof api.workspacePlanDetail>>;
type MemberRole = "admin" | "analyst" | "billing" | "support";

type CreateForm = {
  organizationName: string;
  workspaceSlug: string;
  planId: string;
  status: "trial" | "active" | "restricted";
  region: string;
  ownerName: string;
  ownerEmail: string;
  ownerUsername: string;
  ownerPassword: string;
  includeOwner: boolean;
};

const emptyCreateForm: CreateForm = {
  organizationName: "",
  workspaceSlug: "",
  planId: "",
  status: "trial",
  region: "",
  ownerName: "",
  ownerEmail: "",
  ownerUsername: "",
  ownerPassword: "",
  includeOwner: true
};

type EditForm = {
  workspaceName: string;
  organizationName: string;
  region: string;
  dhis2BaseUrl: string;
  supportTier: "standard" | "priority" | "enterprise";
  aggregateMode: "tally" | "rollup" | "both";
};

type MemberForm = {
  name: string;
  email: string;
  username: string;
  role: MemberRole;
  password: string;
};

const emptyMemberForm: MemberForm = { name: "", email: "", username: "", role: "support", password: "" };

export default function AdminWorkspaces() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [packages, setPackages] = useState<SubscriptionPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assignChoice, setAssignChoice] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createForm, setCreateForm] = useState<CreateForm | null>(null);
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editing, setEditing] = useState(false);
  const [memberForm, setMemberForm] = useState<MemberForm | null>(null);
  const [addingMember, setAddingMember] = useState(false);

  const reloadList = async () => {
    const [w, p] = await Promise.all([
      api.workspace(),
      api.subscriptionPackages().catch(() => ({ packages: [] }))
    ]);
    setWorkspaces(w.workspaces ?? []);
    setMembers(w.members ?? []);
    setPackages(p.packages ?? []);
  };

  const onCreateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!createForm) return;
    setCreating(true);
    setError(null);
    try {
      const result = await api.createTenant({
        organizationName: createForm.organizationName,
        workspaceSlug: createForm.workspaceSlug.trim().toLowerCase(),
        planId: createForm.planId || undefined,
        status: createForm.status,
        region: createForm.region.trim() || undefined,
        owner: createForm.includeOwner
          ? {
              name: createForm.ownerName,
              email: createForm.ownerEmail,
              username: createForm.ownerUsername.trim().toLowerCase(),
              password: createForm.ownerPassword
            }
          : undefined
      });
      setBanner(`Created workspace "${result.workspace.workspaceName ?? result.workspace.id}".`);
      setCreateForm(null);
      await reloadList();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reloadList()
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!workspaceId) { setDetail(null); return; }
    setDetailLoading(true);
    api.workspacePlanDetail(workspaceId)
      .then((d) => {
        setDetail(d);
        setAssignChoice(d.package?.id ?? "");
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setDetailLoading(false));
  }, [workspaceId]);

  const onAssignPackage = async () => {
    if (!workspaceId || !assignChoice) return;
    try {
      await api.assignPackage({ workspaceId, planId: assignChoice });
      setBanner(`Assigned package "${assignChoice}" to workspace ${workspaceId}.`);
      const d = await api.workspacePlanDetail(workspaceId);
      setDetail(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const openEditForm = () => {
    if (!detail?.workspace) return;
    const w = detail.workspace as TenantWorkspace & {
      workspaceName?: string;
      organizationName?: string;
      region?: string;
      dhis2BaseUrl?: string;
      supportTier?: "standard" | "priority" | "enterprise";
      aggregateMode?: "tally" | "rollup" | "both";
    };
    setEditForm({
      workspaceName: w.workspaceName ?? w.name ?? "",
      organizationName: w.organizationName ?? "",
      region: w.region ?? "",
      dhis2BaseUrl: w.dhis2BaseUrl ?? "",
      supportTier: w.supportTier ?? "standard",
      aggregateMode: w.aggregateMode ?? "both"
    });
  };

  const onEditSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!workspaceId || !editForm) return;
    setEditing(true);
    setError(null);
    try {
      await api.updateTenantSettings(workspaceId, {
        workspaceName: editForm.workspaceName.trim() || undefined,
        organizationName: editForm.organizationName.trim() || undefined,
        region: editForm.region.trim() || undefined,
        dhis2BaseUrl: editForm.dhis2BaseUrl.trim() || undefined,
        supportTier: editForm.supportTier,
        aggregateMode: editForm.aggregateMode
      });
      setBanner(`Updated workspace ${workspaceId}.`);
      setEditForm(null);
      const d = await api.workspacePlanDetail(workspaceId);
      setDetail(d);
      await reloadList();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setEditing(false);
    }
  };

  const onAddMemberSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!workspaceId || !memberForm) return;
    setAddingMember(true);
    setError(null);
    try {
      await api.addTenantMember(workspaceId, {
        name: memberForm.name,
        email: memberForm.email,
        username: memberForm.username.trim().toLowerCase(),
        role: memberForm.role,
        password: memberForm.password
      });
      setBanner(`Added member ${memberForm.username} to workspace ${workspaceId}.`);
      setMemberForm(null);
      await reloadList();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setAddingMember(false);
    }
  };

  const onRemoveMember = async (memberId: string, label: string) => {
    if (!workspaceId) return;
    if (!window.confirm(`Remove member "${label}" from this workspace?`)) return;
    setError(null);
    try {
      await api.removeTenantMember(workspaceId, memberId);
      setBanner(`Removed member ${label}.`);
      await reloadList();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onChangeMemberRole = async (memberId: string, role: MemberRole) => {
    if (!workspaceId) return;
    setError(null);
    try {
      await api.setWorkspaceMemberRole(workspaceId, memberId, role);
      setBanner(`Updated role for member ${memberId} to ${role}.`);
      await reloadList();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const workspaceMembers = workspaceId ? members.filter((m) => m.workspaceId === workspaceId) : [];

  // Status dropdown still pre-filters; free-text search now lives in the
  // TableToolbar so it can drive pagination + export consistently.
  const filteredWorkspaces = useMemo(
    () => workspaces.filter((w) => statusFilter === "all" || w.status === statusFilter),
    [workspaces, statusFilter]
  );
  const tWorkspaces = useTableState(filteredWorkspaces as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "slug", "workspaceName", "region", "status"],
    initialPageSize: 25
  });
  const tWorkspaceMembers = useTableState(workspaceMembers as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "email", "role", "status"],
    initialPageSize: 25
  });

  if (workspaceId) {
    return (
      <div className="page">
        <PageBreadcrumbs />
      <header className="page-head">
          <div>
            <h1>{(detail?.workspace as { workspaceName?: string } | undefined)?.workspaceName ?? detail?.workspace?.name ?? workspaceId}</h1>
            <p>
              <code>{workspaceId}</code>
              {detail?.workspace?.slug ? <> · <code>{detail.workspace.slug}</code></> : null}
              {detail?.workspace?.status ? <> · <span className="pill pill-muted">{detail.workspace.status}</span></> : null}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {detail?.workspace?.status === "suspended" ? (
              <button
                className="btn btn-primary"
                type="button"
                onClick={async () => {
                  try {
                    await api.restoreTenant(workspaceId);
                    setBanner(`Restored workspace ${workspaceId}.`);
                    const d = await api.workspacePlanDetail(workspaceId);
                    setDetail(d);
                    setWorkspaces((prev) =>
                      prev.map((w) => (w.id === workspaceId ? { ...w, status: "active" } : w))
                    );
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : String(e));
                  }
                }}
              >
                Restore
              </button>
            ) : (
              <>
                {detail?.workspace?.status && detail.workspace.status !== "active" && (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={async () => {
                      try {
                        await api.activateTenant(workspaceId);
                        setBanner(`Activated workspace ${workspaceId}.`);
                        const d = await api.workspacePlanDetail(workspaceId);
                        setDetail(d);
                        setWorkspaces((prev) =>
                          prev.map((w) => (w.id === workspaceId ? { ...w, status: "active" } : w))
                        );
                      } catch (e) {
                        setError(e instanceof ApiError ? e.message : String(e));
                      }
                    }}
                  >
                    Activate
                  </button>
                )}
                <button
                  className="btn"
                  type="button"
                  onClick={async () => {
                    const reason = window.prompt("Reason (optional)") ?? undefined;
                    if (reason === undefined) return;
                    try {
                      await api.suspendTenant(workspaceId, reason);
                      setBanner(`Suspended workspace ${workspaceId}.`);
                      const d = await api.workspacePlanDetail(workspaceId);
                      setDetail(d);
                      setWorkspaces((prev) =>
                        prev.map((w) => (w.id === workspaceId ? { ...w, status: "suspended" } : w))
                      );
                    } catch (e) {
                      setError(e instanceof ApiError ? e.message : String(e));
                    }
                  }}
                >
                  Suspend
                </button>
              </>
            )}
            <button className="btn" type="button" onClick={openEditForm} disabled={!detail?.workspace}>Edit details</button>
            <button className="btn" type="button" onClick={() => navigate("/admin/workspaces")}>← All workspaces</button>
          </div>
        </header>

        {error && <div className="alert alert-error">{error}</div>}
        {banner && <div className="alert alert-ok">{banner}</div>}

        {detailLoading ? <SkeletonTable rows={5} columns={4} /> : detail && (
          <>
            <section className="section">
              <div className="section-head"><h2>Plan & usage</h2></div>
              <div className="stat-grid">
                <div className="stat">
                  <div className="stat-label">Assigned package</div>
                  <div className="stat-value">{detail.package?.name ?? "— none —"}</div>
                  {detail.package && <div className="stat-hint">${detail.package.price}{detail.package.waived ? " · waived" : ""}</div>}
                </div>
                <div className="stat">
                  <div className="stat-label">Subscription</div>
                  <div className="stat-value">{detail.subscription?.status ?? "—"}</div>
                  {detail.subscription && <div className="stat-hint">Renews {detail.subscription.renewalDate}</div>}
                </div>
                {Object.entries(detail.usage).map(([k, v]) => {
                  const limitKey = k as keyof typeof detail.limits;
                  const limit = detail.limits[limitKey] as number | undefined;
                  const over = typeof limit === "number" && v > limit;
                  return (
                    <div key={k} className="stat">
                      <div className="stat-label">{k}</div>
                      <div className="stat-value" style={{ color: over ? "#b91c1c" : undefined }}>{v}</div>
                      {typeof limit === "number" && <div className="stat-hint">limit {limit}</div>}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="section">
              <div className="section-head"><h2>Reassign package</h2></div>
              <div className="card">
                <div className="card-body" style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <select value={assignChoice} onChange={(e) => setAssignChoice(e.target.value)}>
                    <option value="">— select —</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
                  </select>
                  <button type="button" className="btn btn-primary" onClick={onAssignPackage} disabled={!assignChoice}>
                    Assign
                  </button>
                </div>
              </div>
            </section>

            <section className="section">
              <div className="section-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2>Members ({workspaceMembers.length})</h2>
                <button className="btn btn-primary btn-sm" type="button" onClick={() => setMemberForm({ ...emptyMemberForm })}>
                  Add member
                </button>
              </div>
              {workspaceMembers.length === 0 ? (
                <EmptyState
                  icon="👤"
                  title="No members in this workspace"
                  description="Use “Add member” to provision a teammate directly. They’ll sign in with the username and temporary password you set."
                />
              ) : (
                <div>
                  <TableToolbar
            hideExport
                    search={tWorkspaceMembers.search}
                    setSearch={tWorkspaceMembers.setSearch}
                    page={tWorkspaceMembers.page}
                    setPage={tWorkspaceMembers.setPage}
                    pageSize={tWorkspaceMembers.pageSize}
                    setPageSize={tWorkspaceMembers.setPageSize}
                    totalPages={tWorkspaceMembers.totalPages}
                    totalRows={tWorkspaceMembers.totalRows}
                    searchPlaceholder="Search name / email / role…"
                  />
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(tWorkspaceMembers.paged as unknown as WorkspaceMember[]).map((m) => {
                        const roleStr = String(m.role ?? "");
                        const isOwner = roleStr === "owner";
                        return (
                          <tr key={m.id}>
                            <td>{m.name ?? "—"}</td>
                            <td className="soft">{m.email ?? "—"}</td>
                            <td>
                              {isOwner ? (
                                <span className="pill pill-info">owner</span>
                              ) : (
                                <select
                                  value={roleStr || "support"}
                                  onChange={(e) => onChangeMemberRole(m.id, e.target.value as MemberRole)}
                                >
                                  <option value="admin">admin</option>
                                  <option value="analyst">analyst</option>
                                  <option value="billing">billing</option>
                                  <option value="support">support</option>
                                </select>
                              )}
                            </td>
                            <td>{m.status ?? "—"}</td>
                            <td>
                              {!isOwner && (
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  onClick={() => onRemoveMember(m.id, m.name ?? m.email ?? m.id)}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </>
        )}

        <Drawer
          open={editForm != null}
          title="Edit workspace details"
          onClose={() => !editing && setEditForm(null)}
          width={560}
          asForm
          onSubmit={onEditSubmit}
          footer={(
            <>
              <button type="button" className="btn" disabled={editing} onClick={() => setEditForm(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={editing}>
                {editing ? "Saving…" : "Save changes"}
              </button>
            </>
          )}
        >
          {editForm && (
            <div style={{ display: "grid", gap: 12 }}>
              <div className="field">
                <label>Workspace name</label>
                <input
                  type="text" maxLength={120}
                  value={editForm.workspaceName}
                  onChange={(e) => setEditForm({ ...editForm, workspaceName: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Organization name</label>
                <input
                  type="text" maxLength={120}
                  value={editForm.organizationName}
                  onChange={(e) => setEditForm({ ...editForm, organizationName: e.target.value })}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label>Region</label>
                  <input
                    type="text" maxLength={40}
                    value={editForm.region}
                    onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Support tier</label>
                  <select
                    value={editForm.supportTier}
                    onChange={(e) => setEditForm({ ...editForm, supportTier: e.target.value as EditForm["supportTier"] })}
                  >
                    <option value="standard">Standard</option>
                    <option value="priority">Priority</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label>DHIS2 base URL</label>
                <input
                  type="url" placeholder="https://dhis2.example.org"
                  value={editForm.dhis2BaseUrl}
                  onChange={(e) => setEditForm({ ...editForm, dhis2BaseUrl: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Aggregate mode</label>
                <select
                  value={editForm.aggregateMode}
                  onChange={(e) => setEditForm({ ...editForm, aggregateMode: e.target.value as EditForm["aggregateMode"] })}
                >
                  <option value="both">Both — aggregate-side tally variables AND domain rollups (default)</option>
                  <option value="tally">Tally only — auto-generated 1:1 with data elements; rollup engine suppressed</option>
                  <option value="rollup">Rollup only — aggregate-domain rules are authoritative; tally vars suppressed</option>
                </select>
                <small>
                  Pick the aggregate-side compute path. <strong>Both</strong> keeps current behavior;
                  <strong> Tally</strong> mirrors DHIS2 1:1; <strong>Rollup</strong> uses curated domain
                  rules (cross-program / weighted aggregations). Flipping the mode takes effect on the
                  next sync — pre-existing buckets aren't deleted automatically.
                </small>
              </div>
            </div>
          )}
        </Drawer>

        <Drawer
          open={memberForm != null}
          title="Add workspace member"
          onClose={() => !addingMember && setMemberForm(null)}
          width={520}
          asForm
          onSubmit={onAddMemberSubmit}
          footer={(
            <>
              <button type="button" className="btn" disabled={addingMember} onClick={() => setMemberForm(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={addingMember}>
                {addingMember ? "Adding…" : "Add member"}
              </button>
            </>
          )}
        >
          {memberForm && (
            <div style={{ display: "grid", gap: 12 }}>
                <div className="field">
                  <label>Full name</label>
                  <input
                    type="text" required minLength={2} maxLength={120}
                    value={memberForm.name}
                    onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div className="field">
                    <label>Email</label>
                    <input
                      type="email" required
                      value={memberForm.email}
                      onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label>Username</label>
                    <input
                      type="text" required minLength={3} maxLength={60}
                      value={memberForm.username}
                      onChange={(e) => setMemberForm({ ...memberForm, username: e.target.value })}
                    />
                  </div>
                </div>
                <div className="field">
                  <label>Role</label>
                  <select
                    value={memberForm.role}
                    onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value as MemberRole })}
                  >
                    <option value="admin">admin</option>
                    <option value="analyst">analyst</option>
                    <option value="billing">billing</option>
                    <option value="support">support</option>
                  </select>
                </div>
                <div className="field">
                  <label>Temporary password</label>
                  <input
                    type="password" required minLength={8} maxLength={128}
                    value={memberForm.password}
                    onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                  />
                  <small className="muted">Share securely; the member should change it on first sign-in.</small>
                </div>
            </div>
          )}
        </Drawer>
      </div>
    );
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Workspaces</h1>
          <p>All tenant workspaces on this platform. Drill in to see plan, usage, and members.</p>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => setCreateForm({ ...emptyCreateForm, planId: packages[0]?.id ?? "" })}
        >
          New tenant
        </button>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      <section className="section">
        <div className="card">
          <div className="card-body" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="search"
              placeholder="Filter by name, slug, or id…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: "1 1 240px", minWidth: 200 }}
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="restricted">Restricted</option>
              <option value="suspended">Suspended</option>
            </select>
            <span className="soft">Showing {filteredWorkspaces.length} of {workspaces.length}</span>
          </div>
        </div>
      </section>

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       workspaces.length === 0 ? (
        <EmptyState
          icon="🏢"
          title="No workspaces yet"
          description={<>Self-service tenants register via <code>/signup</code>. Once a workspace is created it appears here with status, plan, and seat usage.</>}
        />
       ) : (
        <div>
          <TableToolbar
            hideExport
            search={tWorkspaces.search}
            setSearch={tWorkspaces.setSearch}
            page={tWorkspaces.page}
            setPage={tWorkspaces.setPage}
            pageSize={tWorkspaces.pageSize}
            setPageSize={tWorkspaces.setPageSize}
            totalPages={tWorkspaces.totalPages}
            totalRows={tWorkspaces.totalRows}
            searchPlaceholder="Search name / slug / id…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Onboarding</th>
                <th>Members</th>
                <th>Region</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(tWorkspaces.paged as unknown as TenantWorkspace[]).map((w) => {
                const count = members.filter((m) => m.workspaceId === w.id).length;
                return (
                  <tr key={w.id}>
                    <td><strong>{(w as { workspaceName?: string }).workspaceName ?? w.name ?? w.id}</strong></td>
                    <td><code>{w.slug ?? "—"}</code></td>
                    <td>{w.status ?? "—"}</td>
                    <td className="soft">{(w as { onboardingStage?: string }).onboardingStage ?? "—"}</td>
                    <td>{count}</td>
                    <td className="soft">{(w as { region?: string }).region ?? "—"}</td>
                    <td className="soft">{w.createdAt ? new Date(w.createdAt).toLocaleDateString() : "—"}</td>
                    <td>
                      <button className="btn btn-sm" type="button" onClick={() => navigate(`/admin/workspaces/${encodeURIComponent(w.id)}`)}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
       )}

      <Drawer
        open={createForm != null}
        title="New tenant"
        onClose={() => !creating && setCreateForm(null)}
        width={560}
        asForm
        onSubmit={onCreateSubmit}
        footer={(
          <>
            <button type="button" className="btn" disabled={creating} onClick={() => setCreateForm(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? "Creating…" : "Create tenant"}
            </button>
          </>
        )}
      >
        {createForm && (
          <div style={{ display: "grid", gap: 12 }}>
              <div className="field">
                <label>Organization name</label>
                <input
                  type="text" required minLength={2} maxLength={120}
                  value={createForm.organizationName}
                  onChange={(e) => setCreateForm({ ...createForm, organizationName: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Workspace slug</label>
                <input
                  type="text" required pattern="[a-z0-9-]+"
                  placeholder="acme-health"
                  value={createForm.workspaceSlug}
                  onChange={(e) => setCreateForm({ ...createForm, workspaceSlug: e.target.value })}
                />
                <small className="muted">Lowercase letters, numbers, dashes. Becomes the workspace id <code>ws-{createForm.workspaceSlug || "slug"}</code>.</small>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label>Initial status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm({ ...createForm, status: e.target.value as CreateForm["status"] })}
                  >
                    <option value="trial">Trial</option>
                    <option value="active">Active</option>
                    <option value="restricted">Restricted</option>
                  </select>
                </div>
                <div className="field">
                  <label>Plan (optional)</label>
                  <select
                    value={createForm.planId}
                    onChange={(e) => setCreateForm({ ...createForm, planId: e.target.value })}
                  >
                    <option value="">— none —</option>
                    {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Region (optional)</label>
                <input
                  type="text" maxLength={40} placeholder="global"
                  value={createForm.region}
                  onChange={(e) => setCreateForm({ ...createForm, region: e.target.value })}
                />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={createForm.includeOwner}
                  onChange={(e) => setCreateForm({ ...createForm, includeOwner: e.target.checked })}
                />
                Create owner account now
              </label>
              {createForm.includeOwner && (
                <div style={{ display: "grid", gap: 12, padding: 12, border: "1px solid var(--c-border, #e5e7eb)", borderRadius: 8 }}>
                  <div className="field">
                    <label>Owner name</label>
                    <input
                      type="text" required minLength={2} maxLength={120}
                      value={createForm.ownerName}
                      onChange={(e) => setCreateForm({ ...createForm, ownerName: e.target.value })}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="field">
                      <label>Owner email</label>
                      <input
                        type="email" required
                        value={createForm.ownerEmail}
                        onChange={(e) => setCreateForm({ ...createForm, ownerEmail: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label>Username</label>
                      <input
                        type="text" required minLength={3} maxLength={60}
                        value={createForm.ownerUsername}
                        onChange={(e) => setCreateForm({ ...createForm, ownerUsername: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Temporary password</label>
                    <input
                      type="password" required minLength={8} maxLength={128}
                      value={createForm.ownerPassword}
                      onChange={(e) => setCreateForm({ ...createForm, ownerPassword: e.target.value })}
                    />
                    <small className="muted">Share this with the owner securely; they should change it on first sign-in.</small>
                  </div>
                </div>
              )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
